import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

export interface SubTask {
  id: number;
  title: string;
  is_completed: boolean;
  order: number;
  created_at: string;
  updated_at: string;
}

export interface SubTaskPayload {
  id?: number;
  title: string;
  is_completed?: boolean;
  order?: number;
}

export interface SubtaskProgress {
  completed: number;
  total: number;
}

export interface Reminder {
  id: number;
  title: string;
  description: string;
  reminder_datetime: string;
  category: string;
  priority: string;
  recurrence: string;
  notes: string;
  is_completed: boolean;
  task_status: 'not_started' | 'in_progress' | 'completed';
  status: 'upcoming' | 'today' | 'overdue' | 'completed';
  subtasks: SubTask[];
  subtask_progress: SubtaskProgress;
  dependant: number | null;
  dependant_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ReminderPayload {
  title: string;
  description?: string;
  reminder_datetime: string;
  category?: string;
  priority?: string;
  recurrence?: string;
  notes?: string;
  is_completed?: boolean;
  task_status?: string;
  dependant?: number | null;
  subtasks?: SubTaskPayload[];
}

export interface ReminderChoices {
  category: Record<string, string>;
  priority: Record<string, string>;
  recurrence: Record<string, string>;
  task_status: Record<string, string>;
}

const reminderService = {
  async getReminders(dependant?: number, opts?: { category?: string; is_completed?: boolean }): Promise<Reminder[]> {
    const params: any = {};
    if (dependant) params.dependant = dependant;
    if (opts?.category) params.category = opts.category;
    if (opts?.is_completed != null) params.is_completed = opts.is_completed;
    const response = await axiosInstance.get(API_ENDPOINTS.reminders.list, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  async getReminder(id: number): Promise<Reminder> {
    const response = await axiosInstance.get(`${API_ENDPOINTS.reminders.list}${id}/`);
    return response.data;
  },

  async createReminder(data: ReminderPayload): Promise<Reminder> {
    const response = await axiosInstance.post(API_ENDPOINTS.reminders.list, data);
    return response.data;
  },

  async updateReminder(id: number, data: ReminderPayload): Promise<Reminder> {
    const response = await axiosInstance.put(`${API_ENDPOINTS.reminders.list}${id}/`, data);
    return response.data;
  },

  async deleteReminder(id: number): Promise<void> {
    await axiosInstance.delete(`${API_ENDPOINTS.reminders.list}${id}/`);
  },

  async markComplete(id: number): Promise<Reminder> {
    const response = await axiosInstance.post(`${API_ENDPOINTS.reminders.list}${id}/mark-complete/`);
    return response.data;
  },

  async getUpcoming(dependant?: number, category?: string): Promise<Reminder[]> {
    const params: any = {};
    if (dependant) params.dependant = dependant;
    if (category) params.category = category;
    const response = await axiosInstance.get(API_ENDPOINTS.reminders.upcoming, { params });
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },

  async getChoices(): Promise<ReminderChoices> {
    const response = await axiosInstance.get(API_ENDPOINTS.reminders.choices);
    return response.data;
  },
};

export default reminderService;
